import { useContext } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import cx from "classnames";
import UserContext from "../../../state/UserContext";
import { EmployeeDashboardUpdateDto } from "../../../types/dtos/dashboards";
import { UserBasicDetailsDto } from "../../../types/dtos/generic";
import { advancedTaskHelper } from "../../../helpers";

interface EmployeeUpdateHeaderProps {
  useShorterText: boolean;
  update: EmployeeDashboardUpdateDto;
  isExpanded: boolean;
}

const getHeaderText = (
  t: TFunction,
  update: EmployeeDashboardUpdateDto,
): string => {
  let headerText = "";
  switch (update.updateType) {
    case "COLLABDOC":
    case "DUALPREPMEETING":
    case "DUALPREPPLANNING":
      if (update.journey != null) {
        headerText = update.journey.journeyTitle;
      }
      break;
    case "NOTASKS":
      if (update.task != null) {
        headerText = t(
          "DashboardUpdate.Employee.Header.NoTasks",
          "You haven't set any #TASKS#",
        ).replace(
          "#TASKS#",
          advancedTaskHelper.ToLowerCase(
            update.task.pluralNameTranslationKeyIdentifier,
          ),
        );
      }
      break;
    case "NOTENOUGHTASKS":
      if (update.task != null) {
        headerText = t(
          "DashboardUpdate.Employee.Header.NotEnoughTasks",
          "You don't have enough #TASKS#",
        ).replace(
          "#TASKS#",
          advancedTaskHelper.ToLowerCase(
            update.task.pluralNameTranslationKeyIdentifier,
          ),
        );
      }
      break;
    case "OVERDUETASKS":
      if (update.task != null) {
        headerText = t(
          "DashboardUpdate.Employee.Header.OverdueTasks",
          "Overdue #TASKS#",
        ).replace(
          "#TASKS#",
          advancedTaskHelper.ToLowerCase(
            update.task.pluralNameTranslationKeyIdentifier,
          ),
        );
      }
      break;
  }
  return headerText;
};

const getSummaryText = (
  t: TFunction,
  useShorterText: boolean,
  update: EmployeeDashboardUpdateDto,
  manager: UserBasicDetailsDto | null | undefined,
): string => {
  let summaryText = "";
  switch (update.updateType) {
    case "COLLABDOC":
      if (update.journey !== null) {
        if (update.journey.showAsWaitingForManager) {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.CollabDoc_WaitingForManager",
            "Waiting for #MANAGER_NAME# to complete",
          );
        } else if (
          update.journey.answerSetApprovalStatus !== null &&
          update.journey.answerSetApprovalStatus !==
            "INITIAL-SAVE-FORM-INCOMPLETE"
        ) {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.CollabDoc_ManagerMadeChanges",
            "#MANAGER_NAME# has made updates for you to review",
          );
        } else {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.CollabDoc_SendChangesToManager",
            "Review and send any amends to #MANAGER_NAME#",
          );
        }
      }
      break;
    case "DUALPREPMEETING":
      if (
        update.journey !== null &&
        update.journey.answerSetApprovalStatus !== null &&
        update.journey.answerSetApprovalStatus !==
          "INITIAL-SAVE-FORM-INCOMPLETE"
      ) {
        summaryText = t(
          "DashboardUpdate.Employee.Summary.DualPrepMeeting_ManagerMadeChanges",
          "#MANAGER_NAME# has made updates for you to review",
        );
      } else {
        summaryText = t(
          "DashboardUpdate.Employee.Summary.DualPrepMeeting_BothPlanningComplete",
          "Both you and #MANAGER_NAME# have completed the planning, you can now meet to discuss",
        );
      }
      break;
    case "DUALPREPPLANNING":
      summaryText = t(
        "DashboardUpdate.Employee.Summary.DualPrepPlanning_CompletePlanning",
        "It's time to complete the planning for your meeting with #MANAGER_NAME#",
      );
      break;
    case "NOTASKS":
      if (update.task != null) {
        const taskName = advancedTaskHelper.ToLowerCase(
          update.task.pluralNameTranslationKeyIdentifier,
        );

        if (useShorterText) {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.NoTasks_Short",
            "Create your #TASKS#",
          ).replace("#TASKS#", taskName);
        } else {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.NoTasks_Long",
            "It looks like you haven't set any #TASKS# yet. Please define your #TASKS# and ensure these are discussed and agreed with your manager",
          ).replaceAll("#TASKS#", taskName);
        }
      }
      break;
    case "NOTENOUGHTASKS":
      if (update.task != null) {
        const taskName = advancedTaskHelper.ToLowerCase(
          update.task.pluralNameTranslationKeyIdentifier,
        );

        if (useShorterText) {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.NotEnoughTasks_Short",
            "Add more #TASKS#",
          ).replace("#TASKS#", taskName);
        } else {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.NotEnoughTasks_Long",
            "It looks like you haven't set enough #TASKS#. Please add more #TASKS# and ensure these are discussed and agreed with your manager",
          ).replaceAll("#TASKS#", taskName);
        }
      }
      break;
    case "OVERDUETASKS":
      if (update.task != null) {
        const taskName = advancedTaskHelper.ToLowerCase(
          update.task.pluralNameTranslationKeyIdentifier,
        );

        if (useShorterText) {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.OverdueTasks_Short",
            "Please ensure your #TASKS# are up to date",
          ).replace("#TASKS#", taskName);
        } else {
          summaryText = t(
            "DashboardUpdate.Employee.Summary.OverdueTasks_Long",
            "Some of your #TASKS# are now overdue. Please take some time to ensure your #TASKS# are up to date. Remember to discuss and agree changes with your manager",
          ).replaceAll("#TASKS#", taskName);
        }
      }
      break;
  }

  const managerFirstName =
    (update.journey != null && update.journey.managerRoleFirstName
      ? update.journey.managerRoleFirstName
      : manager?.firstName) ?? "";

  return summaryText.replace("#MANAGER_NAME#", managerFirstName);
};

function EmployeeUpdateHeader({
  useShorterText,
  update,
  isExpanded,
}: EmployeeUpdateHeaderProps) {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const headerText = getHeaderText(t, update);
  const summaryText = getSummaryText(
    t,
    useShorterText,
    update,
    userContext.user.manager,
  );

  return (
    <>
      <h3 className="heading !mb-0 !mt-0 text-balance text-base font-semibold !text-white">
        {headerText}
      </h3>
      <p
        className={cx(
          "mb-0 mt-0.5 text-sm text-pretty",
          isExpanded ? "text-white/90" : "text-white/80",
        )}
      >
        {summaryText}
      </p>
    </>
  );
}

export default EmployeeUpdateHeader;
